import React, { useRef, useEffect, useCallback } from 'react';
import {
  FlowNode, FlowEdge, DomainKey,
  DOMAIN_COLORS,
} from './moduleFlowData';

// ============================================================
// Internal simulation types
// ============================================================

interface SimNode extends FlowNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  pinned: boolean;
  inDegree: number;
  outDegree: number;
}

interface Props {
  nodesData: FlowNode[];
  edgesData: FlowEdge[];
  searchQuery: string;
  domainFilter: Record<DomainKey, boolean>;
  onNodeSelect: (node: SimNode | null) => void;
  onNodeDoubleClick?: (node: SimNode) => void;
}

// ============================================================
// Physics constants
// ============================================================

const DAMPING = 0.85;
const REPULSION = 8000;
const SPRING_LENGTH = 180;
const SPRING_K = 0.004;
const GRAVITY = 0.002;
const LAYER_FORCE = 0.02;
const INITIAL_ZOOM = 0.65;

// ============================================================
// Helpers (pure)
// ============================================================

function lighten(hex: string, amount: number): string {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  r = Math.min(255, r + amount);
  g = Math.min(255, g + amount);
  b = Math.min(255, b + amount);
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  outerR: number, innerR: number, points: number,
) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = (Math.PI / points) * i - Math.PI / 2;
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  fromX: number, fromY: number, toX: number, toY: number,
  r: number, color: string, lineWidth: number,
) {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const tipX = toX - Math.cos(angle) * r;
  const tipY = toY - Math.sin(angle) * r;
  const headLen = 10 + lineWidth * 2;
  const headAngle = 0.4;

  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX - headLen * Math.cos(angle - headAngle), tipY - headLen * Math.sin(angle - headAngle));
  ctx.lineTo(tipX - headLen * Math.cos(angle + headAngle), tipY - headLen * Math.sin(angle + headAngle));
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

// ============================================================
// Build simulation nodes once
// ============================================================

function buildSimNodes(nodesData: FlowNode[], edgesData: FlowEdge[]): SimNode[] {
  const nodeMap: Record<string, { inDegree: number; outDegree: number }> = {};
  nodesData.forEach(n => { nodeMap[n.id] = { inDegree: 0, outDegree: 0 }; });
  edgesData.forEach(e => {
    if (nodeMap[e.to]) nodeMap[e.to].inDegree++;
    if (nodeMap[e.from]) nodeMap[e.from].outDegree++;
  });

  return nodesData.map(n => {
    const deg = nodeMap[n.id] || { inDegree: 0, outDegree: 0 };
    const totalDegree = deg.inDegree + deg.outDegree;
    const baseR = 22;
    const r = baseR + Math.min(totalDegree * 2.5, 20);
    return {
      ...n,
      x: 0, y: 0,
      vx: 0, vy: 0,
      r,
      pinned: false,
      inDegree: deg.inDegree,
      outDegree: deg.outDegree,
    };
  });
}

function initialLayout(nodes: SimNode[], H: number) {
  const domainOrder: DomainKey[] = ['master', 'sales', 'purchase', 'inventory', 'rd', 'qc', 'finance'];
  const layerGroups: Record<number, Record<string, SimNode[]>> = {};

  nodes.forEach(n => {
    if (!layerGroups[n.layer]) layerGroups[n.layer] = {};
    if (!layerGroups[n.layer][n.domain]) layerGroups[n.layer][n.domain] = [];
    layerGroups[n.layer][n.domain].push(n);
  });

  const layerSpacing = 220;
  const startX = 160;

  for (let layer = 0; layer <= 4; layer++) {
    const groups = layerGroups[layer] || {};
    const x = startX + layer * layerSpacing;
    let yOffset = 80;

    domainOrder.forEach(domain => {
      const domainNodes = groups[domain] || [];
      domainNodes.forEach(n => {
        n.x = x + (Math.random() - 0.5) * 40;
        n.y = yOffset;
        yOffset += 55;
      });
      if (domainNodes.length > 0) yOffset += 20;
    });
  }

  // Center vertically
  let minY = Infinity, maxY = -Infinity;
  nodes.forEach(n => { minY = Math.min(minY, n.y); maxY = Math.max(maxY, n.y); });
  const centerOffset = (H / 2) - (minY + maxY) / 2;
  nodes.forEach(n => { n.y += centerOffset; });
}

// ============================================================
// Component
// ============================================================

const ModuleFlowCanvas: React.FC<Props> = ({ nodesData, edgesData, searchQuery, domainFilter, onNodeSelect, onNodeDoubleClick }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // All mutable state lives in a single ref to avoid stale closures
  const stateRef = useRef({
    nodes: buildSimNodes(nodesData, edgesData),
    nodeMap: {} as Record<string, SimNode>,
    W: 0,
    H: 0,
    camX: 0,
    camY: 0,
    camZoom: INITIAL_ZOOM,
    hoveredNode: null as SimNode | null,
    selectedNode: null as SimNode | null,
    dragging: null as SimNode | null,
    panning: false,
    panStart: { x: 0, y: 0 },
    panCamStart: { x: 0, y: 0 },
    physicsActive: true,
    frameCount: 0,
    animId: 0,
    domainFilter: { ...domainFilter },
    searchQuery: '',
  });

  // Build nodeMap
  useEffect(() => {
    const s = stateRef.current;
    s.nodeMap = {};
    s.nodes.forEach(n => { s.nodeMap[n.id] = n; });
  }, []);

  // Sync props into ref
  useEffect(() => {
    stateRef.current.domainFilter = { ...domainFilter };
  }, [domainFilter]);

  // Search effect: pan to found node
  useEffect(() => {
    const s = stateRef.current;
    s.searchQuery = searchQuery;
    const q = searchQuery.toLowerCase().trim();
    if (!q) {
      s.hoveredNode = null;
      s.selectedNode = null;
      onNodeSelect(null);
      return;
    }
    const found = s.nodes.find(n => n.id.includes(q) || n.name.includes(q));
    if (found) {
      s.selectedNode = found;
      s.hoveredNode = found;
      s.camX = found.x;
      s.camY = found.y;
      s.camZoom = Math.max(s.camZoom, 0.8);
      onNodeSelect(found);
    }
  }, [searchQuery, onNodeSelect]);

  // ============================================================
  // Canvas setup + animation loop
  // ============================================================

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const s = stateRef.current;
    const dpr = window.devicePixelRatio || 1;
    s.W = parent.clientWidth;
    s.H = parent.clientHeight;
    canvas.width = s.W * dpr;
    canvas.height = s.H * dpr;
    canvas.style.width = s.W + 'px';
    canvas.style.height = s.H + 'px';
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const ctxOrNull = canvasEl.getContext('2d');
    if (!ctxOrNull) return;
    // From here ctx and canvas are guaranteed non-null
    const ctx: CanvasRenderingContext2D = ctxOrNull;
    const canvas: HTMLCanvasElement = canvasEl;
    const s = stateRef.current;

    // Initial size
    resizeCanvas();
    initialLayout(s.nodes, s.H);

    // Center camera
    let sumX = 0, sumY = 0;
    s.nodes.forEach(n => { sumX += n.x; sumY += n.y; });
    s.camX = sumX / s.nodes.length;
    s.camY = sumY / s.nodes.length;

    // Build nodeMap
    s.nodeMap = {};
    s.nodes.forEach(n => { s.nodeMap[n.id] = n; });

    // Resize listener
    const onResize = () => {
      resizeCanvas();
    };
    window.addEventListener('resize', onResize);

    // -------------------------------------------------------
    // Coordinate transforms
    // -------------------------------------------------------
    function screenToWorld(sx: number, sy: number) {
      return {
        x: (sx - s.W / 2) / s.camZoom + s.camX,
        y: (sy - s.H / 2) / s.camZoom + s.camY,
      };
    }

    // -------------------------------------------------------
    // Hit test
    // -------------------------------------------------------
    function getNodeAt(sx: number, sy: number): SimNode | null {
      const world = screenToWorld(sx, sy);
      for (let i = s.nodes.length - 1; i >= 0; i--) {
        const n = s.nodes[i];
        if (!s.domainFilter[n.domain]) continue;
        const dx = world.x - n.x;
        const dy = world.y - n.y;
        if (dx * dx + dy * dy < n.r * n.r) return n;
      }
      return null;
    }

    // -------------------------------------------------------
    // Physics simulation
    // -------------------------------------------------------
    function simulate() {
      if (!s.physicsActive) return;
      const cx = s.W / 2 / s.camZoom + s.camX - s.W / 2 / s.camZoom;
      const cy = s.H / 2 / s.camZoom + s.camY - s.H / 2 / s.camZoom;
      // Use center of the current viewport in world coords
      const wcx = s.camX;
      const wcy = s.camY;

      for (let i = 0; i < s.nodes.length; i++) {
        const ni = s.nodes[i];
        if (ni.pinned) continue;

        let fx = 0, fy = 0;

        // Repulsion
        for (let j = 0; j < s.nodes.length; j++) {
          if (i === j) continue;
          const nj = s.nodes[j];
          const dx = ni.x - nj.x;
          const dy = ni.y - nj.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          if (dist < 400) {
            const force = REPULSION / (dist * dist);
            fx += (dx / dist) * force;
            fy += (dy / dist) * force;
          }
        }

        // Spring (edges)
        edgesData.forEach(e => {
          let other: SimNode | undefined;
          if (e.from === ni.id && s.nodeMap[e.to]) other = s.nodeMap[e.to];
          else if (e.to === ni.id && s.nodeMap[e.from]) other = s.nodeMap[e.from];
          if (!other) return;

          const dx = other.x - ni.x;
          const dy = other.y - ni.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = SPRING_K * (dist - SPRING_LENGTH);
          fx += (dx / dist) * force;
          fy += (dy / dist) * force;
        });

        // Gravity toward center of graph (use simple average as in original)
        let avgX = 0, avgY = 0;
        s.nodes.forEach(n => { avgX += n.x; avgY += n.y; });
        avgX /= s.nodes.length;
        avgY /= s.nodes.length;
        fx += (avgX - ni.x) * GRAVITY;
        fy += (avgY - ni.y) * GRAVITY;

        // Layer force
        const targetX = 160 + ni.layer * 220;
        fx += (targetX - ni.x) * LAYER_FORCE;

        ni.vx = (ni.vx + fx) * DAMPING;
        ni.vy = (ni.vy + fy) * DAMPING;
        ni.x += ni.vx;
        ni.y += ni.vy;
      }
    }

    // -------------------------------------------------------
    // Edge helpers
    // -------------------------------------------------------
    function getEdgeColor(e: FlowEdge): string {
      if (e.crossDomain) return '#e74c3c';
      const fromNode = s.nodeMap[e.from];
      return fromNode ? DOMAIN_COLORS[fromNode.domain] : '#666';
    }

    function isEdgeVisible(e: FlowEdge): boolean {
      const fn = s.nodeMap[e.from];
      const tn = s.nodeMap[e.to];
      if (!fn || !tn) return false;
      return s.domainFilter[fn.domain] && s.domainFilter[tn.domain];
    }

    // -------------------------------------------------------
    // Draw
    // -------------------------------------------------------
    function draw() {
      ctx.clearRect(0, 0, s.W, s.H);
      ctx.save();
      ctx.translate(s.W / 2, s.H / 2);
      ctx.scale(s.camZoom, s.camZoom);
      ctx.translate(-s.camX, -s.camY);

      // Determine highlighted set
      const highlightSet = new Set<string>();
      const highlightEdges = new Set<number>();
      const activeNode = s.hoveredNode || s.selectedNode;

      if (activeNode) {
        highlightSet.add(activeNode.id);
        edgesData.forEach((e, idx) => {
          if (e.from === activeNode.id || e.to === activeNode.id) {
            highlightEdges.add(idx);
            highlightSet.add(e.from);
            highlightSet.add(e.to);
          }
        });
      }

      // Draw edges
      edgesData.forEach((e, idx) => {
        if (!isEdgeVisible(e)) return;
        const fromNode = s.nodeMap[e.from];
        const toNode = s.nodeMap[e.to];
        if (!fromNode || !toNode) return;

        const highlighted = highlightEdges.has(idx);
        const dimmed = !!activeNode && !highlighted;
        const baseWidth = Math.max(1, Math.min(e.strength * 0.8, 6));
        const lineWidth = highlighted ? baseWidth + 1.5 : baseWidth;
        const alpha = dimmed ? 0.08 : (highlighted ? 0.9 : 0.35);
        const color = getEdgeColor(e);

        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = lineWidth;

        if (e.crossDomain) {
          ctx.setLineDash([8, 5]);
        } else {
          ctx.setLineDash([]);
        }

        // Curved edges
        const dx = toNode.x - fromNode.x;
        const dy = toNode.y - fromNode.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const midX = (fromNode.x + toNode.x) / 2;
        const midY = (fromNode.y + toNode.y) / 2;
        const perpX = -dy / dist * 15;
        const perpY = dx / dist * 15;

        ctx.moveTo(fromNode.x, fromNode.y);
        ctx.quadraticCurveTo(midX + perpX, midY + perpY, toNode.x, toNode.y);
        ctx.stroke();

        // Arrow head
        if (!dimmed || !activeNode) {
          drawArrowHead(ctx, midX + perpX, midY + perpY, toNode.x, toNode.y, toNode.r + 2, color, baseWidth);
        }

        // Edge label (tables) - only when highlighted
        if (highlighted && e.tables !== '--') {
          const labelX = midX + perpX;
          const labelY = midY + perpY - 8;
          ctx.globalAlpha = 0.9;
          ctx.font = '10px monospace';
          const textW = ctx.measureText(e.tables).width + 6;
          ctx.fillStyle = 'rgba(20,20,40,0.85)';
          ctx.fillRect(labelX - textW / 2, labelY - 8, textW, 14);
          ctx.fillStyle = '#8be9fd';
          ctx.textAlign = 'center';
          ctx.fillText(e.tables, labelX, labelY + 2);
        }

        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
      });

      // Draw nodes
      s.nodes.forEach(n => {
        if (!s.domainFilter[n.domain]) return;

        const highlighted = highlightSet.has(n.id);
        const dimmed = !!activeNode && !highlighted;
        const alpha = dimmed ? 0.15 : 1;
        ctx.globalAlpha = alpha;

        const color = DOMAIN_COLORS[n.domain];
        const r = n.r;

        // Shadow for hub
        if (n.isHub) {
          ctx.shadowColor = '#f1c40f';
          ctx.shadowBlur = 20;
        }

        // Fill
        ctx.beginPath();
        if (n.isHub) {
          drawStar(ctx, n.x, n.y, r + 6, r - 2, 8);
        } else {
          ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        }

        // Gradient fill
        const grad = ctx.createRadialGradient(n.x - r * 0.3, n.y - r * 0.3, r * 0.1, n.x, n.y, r);
        grad.addColorStop(0, lighten(color, 40));
        grad.addColorStop(1, color);
        ctx.fillStyle = grad;
        ctx.fill();

        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Border based on status
        ctx.lineWidth = 3;
        if (n.status === 'done') {
          ctx.strokeStyle = '#2ecc71';
          ctx.setLineDash([]);
        } else if (n.status === 'progress') {
          ctx.strokeStyle = '#f39c12';
          ctx.setLineDash([]);
        } else {
          ctx.strokeStyle = '#555';
          ctx.setLineDash([4, 3]);
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // Hover/select ring
        if (n === s.hoveredNode || n === s.selectedNode) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, r + 5, 0, Math.PI * 2);
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        // Done checkmark
        if (n.status === 'done') {
          ctx.font = 'bold 11px sans-serif';
          ctx.fillStyle = '#2ecc71';
          ctx.textAlign = 'center';
          ctx.fillText('\u2713', n.x + r - 4, n.y - r + 8);
        }

        // Label - Form ID
        ctx.font = 'bold 11px "Segoe UI", sans-serif';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(n.id, n.x, n.y - 5);

        // Chinese name (smaller)
        ctx.font = '10px "Segoe UI", "Microsoft JhengHei", sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        const displayName = n.name.length > 8 ? n.name.substring(0, 7) + '..' : n.name;
        ctx.fillText(displayName, n.x, n.y + 9);

        ctx.globalAlpha = 1;
      });

      ctx.restore();
    }

    // -------------------------------------------------------
    // Animation loop
    // -------------------------------------------------------
    function loop() {
      if (s.frameCount < 300) {
        simulate();
      } else if (s.frameCount % 3 === 0) {
        simulate();
      }
      draw();
      s.frameCount++;
      s.animId = requestAnimationFrame(loop);
    }
    s.animId = requestAnimationFrame(loop);

    // -------------------------------------------------------
    // Mouse event helpers
    // -------------------------------------------------------
    function getCanvasCoords(e: MouseEvent): { cx: number; cy: number } {
      const rect = canvas.getBoundingClientRect();
      return { cx: e.clientX - rect.left, cy: e.clientY - rect.top };
    }

    function onMouseDown(e: MouseEvent) {
      const { cx, cy } = getCanvasCoords(e);
      const node = getNodeAt(cx, cy);
      if (node) {
        s.dragging = node;
        s.dragging.pinned = true;
      } else {
        s.panning = true;
        s.panStart = { x: e.clientX, y: e.clientY };
        s.panCamStart = { x: s.camX, y: s.camY };
      }
    }

    function onMouseMove(e: MouseEvent) {
      const { cx, cy } = getCanvasCoords(e);
      if (s.dragging) {
        const world = screenToWorld(cx, cy);
        s.dragging.x = world.x;
        s.dragging.y = world.y;
        s.dragging.vx = 0;
        s.dragging.vy = 0;
      } else if (s.panning) {
        const dx = (e.clientX - s.panStart.x) / s.camZoom;
        const dy = (e.clientY - s.panStart.y) / s.camZoom;
        s.camX = s.panCamStart.x - dx;
        s.camY = s.panCamStart.y - dy;
      } else {
        const node = getNodeAt(cx, cy);
        s.hoveredNode = node;
        canvas.style.cursor = node ? 'pointer' : 'grab';
      }
    }

    function onMouseUp() {
      if (s.dragging) {
        s.dragging.pinned = false;
        s.dragging = null;
      }
      s.panning = false;
    }

    function onClick(e: MouseEvent) {
      const { cx, cy } = getCanvasCoords(e);
      const node = getNodeAt(cx, cy);
      if (node) {
        s.selectedNode = node;
        onNodeSelect(node);
      } else {
        s.selectedNode = null;
        onNodeSelect(null);
      }
    }

    function onDblClick(e: MouseEvent) {
      const { cx, cy } = getCanvasCoords(e);
      const node = getNodeAt(cx, cy);
      if (node && onNodeDoubleClick) {
        onNodeDoubleClick(node);
      }
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const { cx, cy } = getCanvasCoords(e);
      const zoomFactor = e.deltaY > 0 ? 0.92 : 1.08;
      const newZoom = Math.max(0.15, Math.min(3, s.camZoom * zoomFactor));
      const worldBefore = screenToWorld(cx, cy);
      s.camZoom = newZoom;
      const worldAfter = screenToWorld(cx, cy);
      s.camX -= (worldAfter.x - worldBefore.x);
      s.camY -= (worldAfter.y - worldBefore.y);
    }

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('dblclick', onDblClick);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(s.animId);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('dblclick', onDblClick);
      canvas.removeEventListener('wheel', onWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeCanvas, onNodeSelect, onNodeDoubleClick, nodesData, edgesData]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', background: '#1a1a2e', width: '100%', height: '100%' }}
    />
  );
};

export default ModuleFlowCanvas;
